import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Pencil, Trash2, Search, Truck, Building2, Phone, Mail, CreditCard, MapPin, FileText, Upload, Download, X, Loader2, AlertTriangle, Store, CheckSquare, Square 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { vendorsAPI } from '@/services/api';
import api from '@/services/api';
import AddressForm from '@/components/AddressForm';

const paymentTermsOptions = [
  { value: 'cash', label: 'Cash on Delivery' },
  { value: 'credit_7', label: 'Credit - 7 Days' },
  { value: 'credit_15', label: 'Credit - 15 Days' },
  { value: 'credit_30', label: 'Credit - 30 Days' },
  { value: 'credit_45', label: 'Credit - 45 Days' },
  { value: 'credit_60', label: 'Credit - 60 Days' }
];

const initialFormData = {
  code: '',
  name: '',
  legal_name: '',
  gstin: '',
  pan: '',
  iec_code: '',
  payment_terms: 'credit_30',
  credit_limit: 0,
  opening_balance: 0,
  address: {
    address_line1: '',
    address_line2: '',
    country: 'India',
    state: '',
    district: '',
    area: '',
    pincode: ''
  },
  contact: {
    phone: '',
    email: '',
    alternate_phone: ''
  },
  bank_name: '',
  bank_account: '',
  bank_ifsc: '',
  agreement_url: '',
  // Vendor type and store mapping
  vendor_type: 'central',
  mapped_store_ids: [],
  // Franchise direct purchase flag
  allow_franchise_direct_purchase: false
};

const VendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [vendorTypeFilter, setVendorTypeFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [uploadingAgreement, setUploadingAgreement] = useState(false);
  const [storageConfigured, setStorageConfigured] = useState(false);
  const agreementFileRef = useRef(null);
  const { toast } = useToast();

  const checkStorageConfig = useCallback(async () => {
    try {
      const response = await api.get('/assets/settings');
      setStorageConfigured(response.data?.is_configured || false);
    } catch {
      setStorageConfigured(false);
    }
  }, []);

  const fetchStores = useCallback(async () => {
    try {
      const response = await api.get('/stores?is_active=true');
      // Filter only owned stores (not franchise)
      const ownedStores = (response.data || []).filter(s => s.store_type !== 'franchise');
      setStores(ownedStores);
    } catch (error) {
      console.error('Failed to fetch stores:', error);
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const params = { search };
      if (vendorTypeFilter !== 'all') {
        params.vendor_type = vendorTypeFilter;
      }
      const response = await vendorsAPI.list(params);
      setVendors(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch vendors', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, vendorTypeFilter, toast]);

  useEffect(() => { fetchVendors(); checkStorageConfig(); fetchStores(); }, [fetchVendors, checkStorageConfig, fetchStores]);

  const handleAgreementUpload = async (file) => {
    if (!storageConfigured) {
      // Fallback to base64 if storage not configured
      const reader = new FileReader();
      reader.onload = () => {
        setFormData(p => ({ ...p, agreement_url: reader.result }));
        toast({ title: 'Success', description: 'Agreement uploaded (stored locally)' });
        setUploadingAgreement(false);
      };
      reader.onerror = () => {
        toast({ title: 'Error', description: 'Failed to read file', variant: 'destructive' });
        setUploadingAgreement(false);
      };
      reader.readAsDataURL(file);
      return;
    }

    setUploadingAgreement(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('entity_type', 'vendor_agreements');
      uploadFormData.append('entity_id', selectedVendor?.id || 'new');
      
      const response = await api.post('/assets/upload', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setFormData(p => ({ ...p, agreement_url: response.data.cdn_url }));
        toast({ title: 'Success', description: 'Agreement uploaded to CDN' });
      }
    } catch (error) {
      toast({ 
        title: 'Upload Failed', 
        description: error.response?.data?.detail || 'Failed to upload agreement', 
        variant: 'destructive' 
      });
    } finally {
      setUploadingAgreement(false);
    }
  };

  const handleOpenDialog = async (vendor = null) => {
    if (vendor) {
      setSelectedVendor(vendor);
      setFormData({
        code: vendor.code,
        name: vendor.name,
        legal_name: vendor.legal_name || '',
        gstin: vendor.gstin || '',
        pan: vendor.pan || '',
        iec_code: vendor.iec_code || '',
        payment_terms: vendor.payment_terms || 'credit_30',
        credit_limit: vendor.credit_limit || 0,
        opening_balance: vendor.opening_balance || 0,
        address: {
          address_line1: vendor.address?.address_line1 || '',
          address_line2: vendor.address?.address_line2 || '',
          country: vendor.address?.country || 'India',
          state: vendor.address?.state || '',
          district: vendor.address?.district || vendor.address?.city || '',
          area: vendor.address?.area || '',
          pincode: vendor.address?.pincode || ''
        },
        contact: vendor.contact || initialFormData.contact,
        bank_name: vendor.bank_name || '',
        bank_account: vendor.bank_account || '',
        bank_ifsc: vendor.bank_ifsc || '',
        agreement_url: vendor.agreement_url || '',
        vendor_type: vendor.vendor_type || 'central',
        mapped_store_ids: vendor.mapped_store_ids || [],
        allow_franchise_direct_purchase: vendor.allow_franchise_direct_purchase || false
      });
    } else {
      setSelectedVendor(null);
      // Auto-generate code for new vendor
      try {
        const nextCode = await vendorsAPI.getNextCode();
        setFormData({ ...initialFormData, code: nextCode.code });
      } catch (error) {
        console.error('Error getting next code:', error);
        setFormData(initialFormData);
      }
    }
    setActiveTab('basic');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast({ title: 'Validation Error', description: 'Vendor Name is required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      // Map district to city for backward compatibility
      const payload = {
        ...formData,
        address: {
          ...formData.address,
          city: formData.address.district
        }
      };

      if (selectedVendor) {
        await vendorsAPI.update(selectedVendor.id, payload);
        toast({ title: 'Success', description: 'Vendor updated successfully' });
      } else {
        await vendorsAPI.create(payload);
        toast({ title: 'Success', description: 'Vendor created successfully' });
      }
      setDialogOpen(false);
      fetchVendors();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to save vendor', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await vendorsAPI.delete(selectedVendor.id);
      toast({ title: 'Success', description: 'Vendor deactivated successfully' });
      setDeleteDialogOpen(false);
      fetchVendors();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete vendor', variant: 'destructive' });
    }
  };

  const handleAddressChange = (newAddress) => {
    setFormData(prev => ({ ...prev, address: newAddress }));
  };

  const updateContactField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  const getPaymentTermsLabel = (value) => {
    const option = paymentTermsOptions.find(o => o.value === value);
    return option?.label || value;
  };

  const formatAddress = (addr) => {
    if (!addr) return '';
    const parts = [];
    if (addr.area) parts.push(addr.area);
    if (addr.district || addr.city) parts.push(addr.district || addr.city);
    if (addr.state) parts.push(addr.state);
    if (addr.pincode) parts.push(addr.pincode);
    return parts.join(', ');
  };

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Vendors</h1>
          <p className="text-gray-500">Manage your suppliers and vendors</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-teal-500 hover:bg-teal-600 text-white shadow-lg shadow-teal-200" data-testid="add-vendor-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Vendor
        </Button>
      </div>

      <div className="mb-6 space-y-4">
        {/* Vendor Type Filter Tabs */}
        <div className="flex gap-2">
          <Button 
            variant={vendorTypeFilter === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setVendorTypeFilter('all')}
            className={vendorTypeFilter === 'all' ? 'bg-teal-500 hover:bg-teal-600' : ''}
          >
            All Vendors
          </Button>
          <Button 
            variant={vendorTypeFilter === 'central' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setVendorTypeFilter('central')}
            className={vendorTypeFilter === 'central' ? 'bg-blue-500 hover:bg-blue-600' : ''}
          >
            <Building2 className="w-4 h-4 mr-1" /> Central
          </Button>
          <Button 
            variant={vendorTypeFilter === 'local' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setVendorTypeFilter('local')}
            className={vendorTypeFilter === 'local' ? 'bg-amber-500 hover:bg-amber-600' : ''}
          >
            <Store className="w-4 h-4 mr-1" /> Local (Own Stores)
          </Button>
        </div>
        
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search vendors..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white border-gray-300 text-gray-800 focus:border-teal-500" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : vendors.length === 0 ? (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="py-12 text-center">
            <Truck className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No vendors found</p>
            <Button onClick={() => handleOpenDialog()} variant="link" className="text-teal-600 mt-2">
              Add your first vendor
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vendors.map(vendor => (
            <Card key={vendor.id} className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`vendor-card-${vendor.code}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-gray-800 text-lg">{vendor.name}</CardTitle>
                    <p className="text-sm text-teal-600 font-medium mt-1">{vendor.code}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={vendor.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}>
                      {vendor.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {vendor.vendor_type === 'local' ? (
                      <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1">
                        <Store className="w-3 h-3" /> Local
                      </Badge>
                    ) : (
                      <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> Central
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {vendor.gstin && (
                  <div className="text-xs text-gray-500">GSTIN: {vendor.gstin}</div>
                )}
                {vendor.iec_code && (
                  <div className="text-xs text-gray-500">IEC: {vendor.iec_code}</div>
                )}
                
                {/* Show mapped stores for local vendors */}
                {vendor.vendor_type === 'local' && vendor.mapped_store_ids?.length > 0 && (
                  <div className="p-2 rounded bg-amber-50 border border-amber-100">
                    <p className="text-xs text-amber-700 font-medium mb-1">Mapped Stores:</p>
                    <div className="flex flex-wrap gap-1">
                      {vendor.mapped_store_ids.slice(0, 3).map(storeId => {
                        const store = stores.find(s => s.id === storeId);
                        return store ? (
                          <Badge key={storeId} variant="outline" className="text-xs bg-white">
                            {store.code}
                          </Badge>
                        ) : null;
                      })}
                      {vendor.mapped_store_ids.length > 3 && (
                        <Badge variant="outline" className="text-xs bg-white">
                          +{vendor.mapped_store_ids.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
                
                {vendor.contact?.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600">{vendor.contact.phone}</span>
                  </div>
                )}

                {vendor.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                    <span className="text-gray-600">{formatAddress(vendor.address)}</span>
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm">
                  <CreditCard className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">{getPaymentTermsLabel(vendor.payment_terms)}</span>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                  <div>
                    <p className="text-xs text-gray-500">Balance</p>
                    <p className={`font-medium ${vendor.current_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      ₹{Math.abs(vendor.current_balance || 0).toLocaleString()}
                      {vendor.current_balance > 0 ? ' (Payable)' : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                      onClick={() => handleOpenDialog(vendor)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => { setSelectedVendor(vendor); setDeleteDialogOpen(true); }}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">{selectedVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-gray-100 w-full justify-start">
              <TabsTrigger value="basic" className="data-[state=active]:bg-white">Basic Info</TabsTrigger>
              <TabsTrigger value="type" className="data-[state=active]:bg-white">Type & Stores</TabsTrigger>
              <TabsTrigger value="address" className="data-[state=active]:bg-white">Address</TabsTrigger>
              <TabsTrigger value="bank" className="data-[state=active]:bg-white">Bank & Payment</TabsTrigger>
              <TabsTrigger value="agreement" className="data-[state=active]:bg-white">Agreement</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Vendor Code</Label>
                  <Input value={formData.code} onChange={(e) => setFormData(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                    placeholder="Auto-generated" className="bg-gray-50 border-gray-300" disabled={!!selectedVendor} />
                  <p className="text-xs text-gray-500">Leave blank to auto-generate</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Vendor Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Vendor Name" className="bg-gray-50 border-gray-300" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Legal Name</Label>
                <Input value={formData.legal_name} onChange={(e) => setFormData(p => ({ ...p, legal_name: e.target.value }))}
                  placeholder="Legal/Registered Name" className="bg-gray-50 border-gray-300" />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">GSTIN (Optional)</Label>
                  <Input value={formData.gstin} onChange={(e) => setFormData(p => ({ ...p, gstin: e.target.value.toUpperCase() }))}
                    placeholder="22AAAAA0000A1Z5" className="bg-gray-50 border-gray-300" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">PAN</Label>
                  <Input value={formData.pan} onChange={(e) => setFormData(p => ({ ...p, pan: e.target.value.toUpperCase() }))}
                    placeholder="AAAAA0000A" className="bg-gray-50 border-gray-300" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">IEC Code</Label>
                  <Input value={formData.iec_code} onChange={(e) => setFormData(p => ({ ...p, iec_code: e.target.value.toUpperCase() }))}
                    placeholder="Import Export Code" className="bg-gray-50 border-gray-300" />
                </div>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Phone</Label>
                  <Input value={formData.contact.phone} onChange={(e) => updateContactField('phone', e.target.value)}
                    placeholder="Phone number" className="bg-gray-50 border-gray-300" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Email</Label>
                  <Input value={formData.contact.email} onChange={(e) => updateContactField('email', e.target.value)}
                    placeholder="Email address" className="bg-gray-50 border-gray-300" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-700">Alternate Phone</Label>
                <Input value={formData.contact.alternate_phone} onChange={(e) => updateContactField('alternate_phone', e.target.value)}
                  placeholder="Alternate phone number" className="bg-gray-50 border-gray-300" />
              </div>
            </TabsContent>

            <TabsContent value="type" className="space-y-4 mt-4">
              {/* Vendor Type Selection */}
              <div className="p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 space-y-4">
                <h3 className="text-sm font-medium text-blue-800 flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Vendor Type
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div 
                    onClick={() => setFormData(p => ({ ...p, vendor_type: 'central', mapped_store_ids: [] }))}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.vendor_type === 'central' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 bg-white hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Building2 className={`w-6 h-6 ${formData.vendor_type === 'central' ? 'text-blue-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="font-medium text-gray-800">Central Vendor</p>
                        <p className="text-xs text-gray-500">For HQ Purchase Orders & GRN</p>
                      </div>
                    </div>
                  </div>
                  <div 
                    onClick={() => setFormData(p => ({ ...p, vendor_type: 'local' }))}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.vendor_type === 'local' 
                        ? 'border-amber-500 bg-amber-50' 
                        : 'border-gray-200 bg-white hover:border-amber-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Store className={`w-6 h-6 ${formData.vendor_type === 'local' ? 'text-amber-600' : 'text-gray-400'}`} />
                      <div>
                        <p className="font-medium text-gray-800">Local Vendor</p>
                        <p className="text-xs text-gray-500">For Own Store Local Purchases</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Store Mapping - Only shown for Local Vendors */}
              {formData.vendor_type === 'local' && (
                <div className="p-4 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-100 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-amber-800 flex items-center gap-2">
                      <Store className="w-4 h-4" />
                      Map to Own Stores
                    </h3>
                    <Badge className="bg-amber-100 text-amber-700">
                      {formData.mapped_store_ids.length} store(s) selected
                    </Badge>
                  </div>
                  <p className="text-xs text-amber-700">
                    Select the own stores that can purchase from this local vendor. Payments will be managed by HQ.
                  </p>
                  
                  {stores.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                      <p>No owned stores found. Only owned stores can be mapped to local vendors.</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {stores.map(store => (
                        <div 
                          key={store.id}
                          onClick={() => {
                            const isSelected = formData.mapped_store_ids.includes(store.id);
                            if (isSelected) {
                              setFormData(p => ({ 
                                ...p, 
                                mapped_store_ids: p.mapped_store_ids.filter(id => id !== store.id) 
                              }));
                            } else {
                              setFormData(p => ({ 
                                ...p, 
                                mapped_store_ids: [...p.mapped_store_ids, store.id] 
                              }));
                            }
                          }}
                          className={`p-3 rounded-lg border cursor-pointer transition-all flex items-center gap-3 ${
                            formData.mapped_store_ids.includes(store.id) 
                              ? 'border-amber-500 bg-amber-50' 
                              : 'border-gray-200 bg-white hover:border-amber-300'
                          }`}
                        >
                          <Checkbox 
                            checked={formData.mapped_store_ids.includes(store.id)}
                            className="pointer-events-none"
                          />
                          <div className="flex-1">
                            <p className="font-medium text-gray-800">{store.name}</p>
                            <p className="text-xs text-gray-500">{store.code} | {store.address?.district || store.address?.city}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Franchise Direct Purchase - Only shown for Central Vendors */}
              {formData.vendor_type === 'central' && (
                <div className="p-4 rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-purple-800 flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      Franchise Direct Purchase
                    </h3>
                  </div>
                  <p className="text-xs text-purple-700">
                    Allow franchise stores to create purchase orders and receive goods directly from this vendor.
                    HQ will monitor all POs and payments.
                  </p>
                  <div 
                    onClick={() => setFormData(p => ({ ...p, allow_franchise_direct_purchase: !p.allow_franchise_direct_purchase }))}
                    className={`p-3 rounded-lg border cursor-pointer transition-all flex items-center gap-3 ${
                      formData.allow_franchise_direct_purchase 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 bg-white hover:border-purple-300'
                    }`}
                  >
                    <Checkbox 
                      checked={formData.allow_franchise_direct_purchase}
                      className="pointer-events-none"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">Allow Franchise Direct Purchase</p>
                      <p className="text-xs text-gray-500">Franchise stores can order directly from this vendor</p>
                    </div>
                    {formData.allow_franchise_direct_purchase && (
                      <Badge className="bg-purple-100 text-purple-700">Enabled</Badge>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="address" className="space-y-4 mt-4">
              <div className="p-4 rounded-lg bg-teal-50 border border-teal-100">
                <h3 className="text-sm font-medium text-teal-800 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Vendor Address
                </h3>
                <AddressForm
                  address={formData.address}
                  onChange={handleAddressChange}
                />
              </div>
            </TabsContent>

            <TabsContent value="bank" className="space-y-4 mt-4">
              {/* Payment Terms */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Payment Terms</Label>
                  <Select value={formData.payment_terms} onValueChange={(v) => setFormData(p => ({ ...p, payment_terms: v }))}>
                    <SelectTrigger className="bg-gray-50 border-gray-300"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {paymentTermsOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Credit Limit (₹)</Label>
                  <Input type="number" value={formData.credit_limit}
                    onChange={(e) => setFormData(p => ({ ...p, credit_limit: parseFloat(e.target.value) || 0 }))}
                    className="bg-gray-50 border-gray-300" />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Opening Balance (₹)</Label>
                  <Input type="number" value={formData.opening_balance}
                    onChange={(e) => setFormData(p => ({ ...p, opening_balance: parseFloat(e.target.value) || 0 }))}
                    className="bg-gray-50 border-gray-300" disabled={!!selectedVendor} />
                </div>
              </div>

              {/* Bank Details */}
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-4">Bank Details</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Bank Name</Label>
                    <Input value={formData.bank_name} onChange={(e) => setFormData(p => ({ ...p, bank_name: e.target.value }))}
                      placeholder="Bank Name" className="bg-white border-gray-300" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Account Number</Label>
                    <Input value={formData.bank_account} onChange={(e) => setFormData(p => ({ ...p, bank_account: e.target.value }))}
                      placeholder="Account Number" className="bg-white border-gray-300" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">IFSC Code</Label>
                    <Input value={formData.bank_ifsc} onChange={(e) => setFormData(p => ({ ...p, bank_ifsc: e.target.value.toUpperCase() }))}
                      placeholder="IFSC Code" className="bg-white border-gray-300" />
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="agreement" className="space-y-4 mt-4">
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                <h3 className="text-sm font-medium text-blue-800 mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Vendor Agreement Document
                </h3>
                
                {formData.agreement_url ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">Agreement Document</p>
                          <p className="text-xs text-gray-500">PDF document uploaded</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-blue-300 text-blue-600 hover:bg-blue-50"
                          onClick={() => window.open(formData.agreement_url, '_blank')}
                        >
                          <Download className="w-4 h-4 mr-1" /> Download
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          onClick={() => setFormData(p => ({ ...p, agreement_url: '' }))}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="border-2 border-dashed border-blue-200 rounded-lg p-6 text-center">
                      <Upload className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600 mb-3">Upload vendor agreement document (PDF)</p>
                      <div className="space-y-2">
                        <input
                          ref={agreementFileRef}
                          type="file"
                          accept=".pdf,image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            
                            if (file.size > 5 * 1024 * 1024) {
                              toast({ title: 'Error', description: 'File size must be less than 5MB', variant: 'destructive' });
                              return;
                            }
                            
                            handleAgreementUpload(file);
                            e.target.value = '';
                          }}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          className="border-blue-300 text-blue-600"
                          onClick={() => agreementFileRef.current?.click()}
                          disabled={uploadingAgreement}
                        >
                          {uploadingAgreement ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                          ) : (
                            <><Upload className="w-4 h-4 mr-2" /> Select File</>
                          )}
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 text-center">
                      Max file size: 5MB. PDF and image files accepted.
                      {!storageConfigured && (
                        <span className="block text-amber-600 mt-1">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          CDN storage not configured - files stored locally
                        </span>
                      )}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-700">
                  <strong>Note:</strong> After saving, a Vendor Registration Invoice will be automatically generated 
                  based on the fee configured in HQ Revenue Settings.
                </p>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300 text-gray-600">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-500 hover:bg-teal-600 text-white">
              {saving ? 'Saving...' : 'Save Vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-800">Deactivate Vendor?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              This will deactivate "{selectedVendor?.name}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300 text-gray-600">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VendorsPage;
